import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { Paper, Stack, Typography } from '@esgian/esgianui';
import { VIEW_BY_TYPE } from '@constants/index';
import { BaselineComparisonChart } from '@components/Charts';
import LookupSelect from '@components/Inputs/LookupSelect';
import DataTypeSelect from '../../../../Inputs/DataTypeSelect';
import { useDispatch, useSelector } from 'react-redux';
import { getDisplayUnit } from '@store/features';
import {
  getBaselineComparisonSlice,
  setAnalyticsFilters
} from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';
import { getMainPageSlice } from '@store/features/filters/MainPageSlice/MainPageSlice';
import { LOOKUP_FOCUS_AREA } from '@lookups';
import { useBaselineComp } from '@hooks/useBaselineComp';
import HourDaySelect from '../../../../Inputs/HourDaySelect';
import { BaselineChartTypeSelect } from '@components/Inputs';
import { useBaselineQueries } from '@hooks';
import AnalyticsComparisonKpis from '@components/Sections/BaselineComparisonSection/SubSections/AnalyticsComparison/AnalyticsComparisonKpis';

function AnalyticsComparison() {
  const displayUnit = useSelector(getDisplayUnit);
  const dispatch = useDispatch();
  const { getAnalyticsSeries } = useBaselineComp();
  const { baselineAnalyticsQueries, focusAreas } = useBaselineQueries();
  const {
    analytics: { dataType, focusArea, viewBy, chartType }
  } = useSelector(getBaselineComparisonSlice);
  const { selectedBaseline } = useSelector(getBaselineComparisonSlice);
  const { selectedRig } = useSelector(getMainPageSlice);
  
  const handleChange = useCallback((val) => {
    dispatch(setAnalyticsFilters(val));
  }, []);

  const hasRun = useRef(false);
  
  useEffect(() => {
    if(hasRun.current) {
      hasRun.current = false;
    }
  }, [selectedBaseline, selectedRig]);

  
  const mainData = baselineAnalyticsQueries[0].data;
  const baselineData = baselineAnalyticsQueries[1].data;
  const mainFocusAreas = focusAreas[0].data;
  const baseFocusAreas = focusAreas[1].data;
  const mergedFocusAreas = mainFocusAreas?.length && baseFocusAreas?.length ? [...new Set([...mainFocusAreas, ...baseFocusAreas])] : [];
  const lookupFocusAreas = LOOKUP_FOCUS_AREA.filter((lfa) => mergedFocusAreas.includes(lfa.key));
  
  const {
    mainSeries = [],
    baseSeries = [],
    categories = [],
    unit,
    max = 0
  } = useMemo(() => {
    if (mainData === null || !baselineData || !mergedFocusAreas?.length) 
      return {};

    return getAnalyticsSeries(mainData, baselineData);
  }, [displayUnit, mainData, baselineData, dataType, mergedFocusAreas, viewBy]);

  useEffect(() => {
    if(lookupFocusAreas?.length && !hasRun.current) {
      dispatch(setAnalyticsFilters({ focusArea: lookupFocusAreas }));
      hasRun.current = true;
      return;
    }
  }, [lookupFocusAreas]);

  const loading = baselineAnalyticsQueries?.some((item) => item.isFetching);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack justifyContent={'space-between'} direction={'row'}>
          <Typography variant={'h6'}>Analytics - comparison of potential saved energy</Typography>
        </Stack>
        <Typography variant={'subtitle1'} bold color={'text.secondary'}>
          Comparing to the Baseline
        </Typography>
        <AnalyticsComparisonKpis
          loading={loading}
          baselineData={baselineData}
          mainData={mainData}
        />
        <Typography variant={'subtitle1'} bold color={'text.secondary'}>
          Comparison over time
        </Typography>
        <BaselineComparisonChart
          chartActions={[
            <LookupSelect
              options={lookupFocusAreas}
              label={'Focus area'}
              key={'focus-area-select'}
              value={focusArea}
              onChange={(val) => {
                handleChange({ focusArea: val });
              }}
              loading={loading}
            />,
            <BaselineChartTypeSelect
              key={'baseline-chart-type-select'}
              value={chartType}
              handleChange={(val) => {
                handleChange({ chartType: val });
              }}
            />,
            <DataTypeSelect
              key={'data-type-select'}
              value={dataType}
              onChange={(val) => {
                handleChange({ dataType: val });
              }}
            />,
            <HourDaySelect
              key={'view-by-select'}
              value={viewBy}
              handleChange={(val) => {
                handleChange({ viewBy: val });
              }}
            />
          ]}
          viewBy={viewBy === VIEW_BY_TYPE.DAILY ? 'day' : 'hour'}
          dataType={dataType}
          yAxisTitle={unit}
          mainMax={max}
          chartType={chartType}
          loading={loading}
          mainSeries={mainSeries}
          baseSeries={baseSeries}
          categories={categories}
        />
      </Stack>
    </Paper>
  );
}

AnalyticsComparison.propTypes = {};

AnalyticsComparison.defaultProps = {};

export default AnalyticsComparison;
